import {useEffect, useState} from "react";
import StatusControl from "../../../shared/StatusControl/StatusControl";
import {Input, Message, toaster, Whisper} from "rsuite";
import {ReadRelay1Command, ReadRelay2Command} from "../../../../Commands";
import {ClockStation} from "../../../../Targets";
import UpdateButton from "../../../shared/UpdateButton/UpdateButton";
import GearIcon from "@rsuite/icons/Gear";
import {getTooltip} from "../../../../shared/Tooltip";
import {RelaySettingsSectionProps, RelayTimeRange} from "../MainPage.model";
import {formatTime, getRelayModeText} from "../MainPage.utils";
import { RelayModeValue } from "../../RelaySettings/RelaySettings.model";

const RelaySettingsSection = ({
                                deviceId,
                                relayDescriptions,
                                relayStatus,
                                isNewVersion = true,
                                onTabChange, executeCommand,
                                duskTime,
                                dawnTime,
                              }: RelaySettingsSectionProps) => {
  const [relayOneTime, setRelayOneTime] = useState<RelayTimeRange | null>(null);
  const [relayTwoTime, setRelayTwoTime] = useState<RelayTimeRange | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const relay0 = relayStatus?.relays?.["0"];
  const relay1 = relayStatus?.relays?.["1"];

  useEffect(() => {
    readData().then();
  }, []);

  useEffect(() => {
    updateRelayTimesWithMode();
  }, [relay0?.mode, relay1?.mode, duskTime, dawnTime]);

  const handleRefreshSection = async () => {
    setRefreshLoading(true);
    try {
      await readData();

      toaster.push(
        <Message type="success" closable>
          Данные реле успешно обновлены
        </Message>,
        {placement: 'topEnd', duration: 2000}
      );

    } catch (error) {
      console.error('Ошибка при обновлении данных реле:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка при обновлении данных реле
        </Message>,
        {placement: 'topEnd', duration: 3000}
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleSettingsClick = () => {
    if (onTabChange) {
      onTabChange('relay');
    }
  };



  const updateRelayTimesWithMode = () => {
    if (relay0?.mode === RelayModeValue.ConnectedToAstronomicalRelay) {
      setRelayOneTime({
        onTime: duskTime,
        offTime: dawnTime
      });
    } else if (relay0?.mode !== undefined) {
      readData().then();
    }

    if (relay1?.mode === RelayModeValue.ConnectedToAstronomicalRelay) {
      setRelayTwoTime({
        onTime: duskTime,
        offTime: dawnTime
      });
    } else if (relay1?.mode !== undefined) {
      readData().then();
    }
  };

  const readData = async () => {
    try {
      const resultRelayOne = await executeCommand(ClockStation, ReadRelay1Command, []);
      const resultRelayTwo = await executeCommand(ClockStation, ReadRelay2Command, []);

      const parseRelayTime = (result?: { Params?: number[] } | null): RelayTimeRange | null => {
        if (!result || !result.Params || result.Params.length < 5) {
          return null;
        }

        const [_, onHour, onMinute, offHour, offMinute] = result.Params;

        const hasTime = onHour !== 0 || onMinute !== 0 || offHour !== 0 || offMinute !== 0;

        if (hasTime) {
          return {
            onTime: {hours: onHour, minutes: onMinute},
            offTime: {hours: offHour, minutes: offMinute}
          };
        }

        return null;
      };

      const relayOneTimeData = parseRelayTime(resultRelayOne);
      const relayTwoTimeData = parseRelayTime(resultRelayTwo);

      const IGNORED_MODES = [
        RelayModeValue.Disabled,
        RelayModeValue.AlwaysOn,
        RelayModeValue.AlwaysOff,
        RelayModeValue.ConnectedToAstronomicalRelay
      ];

      if (relay0?.mode !== undefined && !IGNORED_MODES.includes(relay0.mode)) {
        switch (relay0.mode) {
          case RelayModeValue.ConnectedToTimeRelay1:
            setRelayOneTime(relayOneTimeData);
            break;
          case RelayModeValue.ConnectedToTimeRelay2:
            setRelayOneTime(relayTwoTimeData);
            break;
          default:
        }
      }

      if (relay1?.mode !== undefined && !IGNORED_MODES.includes(relay1.mode)) {
        switch (relay1.mode) {
          case RelayModeValue.ConnectedToTimeRelay1:
            setRelayTwoTime(relayOneTimeData);
            break;
          case RelayModeValue.ConnectedToTimeRelay2:
            setRelayTwoTime(relayTwoTimeData);
            break;
          default:
        }
      }

    } catch (error) {
      console.error('Ошибка при чтении данных реле:', error);
      throw error;
    }
  };

  const getDisplayTime = (relayTime: RelayTimeRange | null, relayMode?: RelayModeValue) => {
    if (relayMode === RelayModeValue.ConnectedToAstronomicalRelay && duskTime && dawnTime) {
      return {
        onTime: duskTime,
        offTime: dawnTime
      };
    }

    return relayTime;
  };

  const displayRelayOneTime = getDisplayTime(relayOneTime, relay0?.mode);
  const displayRelayTwoTime = getDisplayTime(relayTwoTime, relay1?.mode);

  return (
    <div className="fieldset-wrapper mt-3">
      <div className="legend-title main-legend-title">Настройка Реле</div>

      <section className="relay-section">
        <div className="row p-1">
          <div className="col-md-12 text-end">
            <UpdateButton
              loading={refreshLoading}
              onRefresh={handleRefreshSection}
            />
          </div>
        </div>
        <div className="row" style={{width: '100%'}}>
          {/* Реле 1 */}
          <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12 mb-4">
            <div className="fieldset-wrapper control bg-white">
              <div className="legend-title">1 Канал реле</div>
              <div className="relay-wrapper">
                <div className="relay-container">

                  <div className="relay-mode mb-1 row">
                    <div className="col-md-2 col-2">
                      <div className="relay-indicator">
                        <StatusControl status={relay0?.enabled ? "online" : "offline"}/>
                      </div>
                    </div>
                    <div className="col-md-10 col-10">
                      <div className="relay-mode-value">
                        {getRelayModeText(relay0?.mode)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 col-6 text-center">
                    <div className="relay-time-title">
                      Вкл
                    </div>
                    <div className="text-center">
                      <input
                        readOnly={true}
                        className="settings-form-control relay-time-control time-input form-control"
                        value={formatTime(displayRelayOneTime?.offTime)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 col-6 text-center">
                    <div className="relay-time-title">
                      Выкл
                    </div>
                    <div className="text-center">
                      <input
                        readOnly={true}
                        className="settings-form-control relay-time-control time-input form-control"
                        value={formatTime(displayRelayOneTime?.onTime)}
                      />
                    </div>
                  </div>
                </div>

                <div className="relay-description mt-1">
                  <Input
                    value={relayDescriptions?.channel1 || "Описание не указано"}
                    placeholder="Описание для реле 1"
                    disabled={true}
                    className="relay-description-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Реле 2 */}
          <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12 mb-4">
            <div className="fieldset-wrapper control h-100 bg-white">
              <div className="legend-title">2 Канал реле</div>
              <div className="relay-wrapper">
                <div className="relay-container">

                  <div className="relay-mode mb-1 row">
                    <div className="col-md-2 col-2">
                      <div className="relay-indicator">
                        <StatusControl status={relay1?.enabled ? "online" : "offline"}/>
                      </div>
                    </div>
                    <div className="col-md-10 col-10">
                      <div className="relay-mode-value">
                        {getRelayModeText(relay1?.mode)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 col-6 text-center">
                    <div className="relay-time-title">
                      Вкл
                    </div>
                    <div className="text-center">
                      <input
                        readOnly={true}
                        className="settings-form-control relay-time-control time-input form-control"
                        value={formatTime(displayRelayTwoTime?.offTime)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 col-6 text-center">
                    <div className="relay-time-title">
                      Выкл
                    </div>
                    <div className="text-center">
                      <input
                        readOnly={true}
                        className="settings-form-control relay-time-control time-input form-control"
                        value={formatTime(displayRelayTwoTime?.onTime)}
                      />
                    </div>
                  </div>
                </div>

                <div className="relay-description mt-1">
                  <Input
                    value={relayDescriptions?.channel2 || "Описание не указано"}
                    placeholder="Описание для реле 2"
                    disabled={true}
                    className="relay-description-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="settings" style={{padding: '5px'}}>
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleSettingsClick();
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}>
            <GearIcon className="gear-icon"></GearIcon>
            </Whisper>
          </a>
        </div>
      </section>
    </div>
  );
};

export default RelaySettingsSection;
